import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import "@/App.css";

import Welcome from "@/pages/Welcome";
import Dashboard from "@/pages/Dashboard";
import AIChat from "@/pages/AIChat";
import OperatorChat from "@/pages/OperatorChat";
import MenuPage from "@/pages/MenuPage";
import PromoInfo from "@/pages/PromoInfo";
import Feedback from "@/pages/Feedback";
import StaffDashboard from "@/pages/StaffDashboard";
import { BottomNav } from "@/components/BottomNav";

const STORAGE_KEY = "gt_guest";
const LANG_KEY = "gt_lang";

function loadGuest() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const g = JSON.parse(raw);
    if (!g.title) g.title = "Mr";
    return g;
  } catch {
    return null;
  }
}

function App() {
  const [guest, setGuest] = useState(loadGuest);
  const [lang, setLangState] = useState(() => localStorage.getItem(LANG_KEY) || "id");
  const [page, setPage] = useState(guest ? "dashboard" : "welcome");
  const isStaff = typeof window !== "undefined" && window.location.search.includes("staff");

  useEffect(() => {
    document.title = "Golden Tulip Holland Resort — Concierge";
  }, []);

  const setLang = (l) => {
    localStorage.setItem(LANG_KEY, l);
    setLangState(l);
  };

  const start = (title, name, room) => {
    const g = {
      title,
      name,
      room,
      sessionId: `${room}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(g));
    setGuest(g);
    setPage("dashboard");
  };

  const logout = (confirmMsg) => {
    if (!window.confirm(confirmMsg)) return;
    localStorage.removeItem(STORAGE_KEY);
    setGuest(null);
    setPage("welcome");
  };

  const navigate = (p) => setPage(p);

  if (isStaff) {
    return (
      <div className="App min-h-screen w-full flex justify-center bg-[#E3EDF5]">
        <div className="w-full max-w-[430px] lg:max-w-[720px] min-h-screen bg-white relative overflow-hidden shadow-[0_0_40px_rgba(26,26,46,0.08)]">
          <StaffDashboard lang={lang} setLang={setLang} />
        </div>
      </div>
    );
  }

  const renderPage = () => {
    if (!guest || page === "welcome") return <Welcome onStart={start} lang={lang} setLang={setLang} />;
    const common = { guest, navigate, lang, setLang };
    switch (page) {
      case "dashboard":
        return <Dashboard {...common} onLogout={() => logout(page)} />;
      case "ai":
        return <AIChat {...common} />;
      case "operator":
        return <OperatorChat {...common} />;
      case "menu":
        return <MenuPage {...common} />;
      case "promo":
        return <PromoInfo {...common} />;
      case "feedback":
        return <Feedback {...common} />;
      default:
        return <Dashboard {...common} onLogout={() => logout(page)} />;
    }
  };

  const showNav = guest && page !== "welcome";
  const isChat = page === "ai" || page === "operator";

  return (
    <div className="App min-h-screen w-full flex justify-center bg-[#E3EDF5]">
      <div className="w-full max-w-[430px] min-h-screen bg-white relative overflow-hidden shadow-[0_0_40px_rgba(26,26,46,0.08)]">
        <AnimatePresence mode="wait">
          <motion.div
            key={page + lang}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.22, ease: "easeInOut" }}
            className={isChat ? "" : "min-h-screen"}
          >
            {renderPage()}
          </motion.div>
        </AnimatePresence>

        {showNav && !isChat && <div className="h-[72px]" />}
        {showNav && <BottomNav page={page} navigate={navigate} lang={lang} />}
      </div>
    </div>
  );
}

export default App;
